// Services
import { apiInstance, gql } from "@/services/httpAdapter";

// Utils
import { errorLogger } from "@Common/helpers/logger";

// Types
import type { GraphqlQueryResponse } from "@Common/api/types/GraphqlQueryResponse";
import type { IGetSalesPerformanceResponse } from "@DataPages/types/salesOperations/salesPerformance/model";
import type { ISalesPerformanceExport } from "@DataPages/types/salesOperations/salesPerformance/models/crossStoreReconciliation";

export async function getSalesPerformancesByCommissionCycle(locationId: string, id: string) {
	const query = gql`
		query getSalesPerformancesByCommissionCycle($locationId: String!, $id: String!) {
			getSalesPerformancesByCommissionCycle(locationId: $locationId, id: $id) {
				salesPerformance {
					_id
					dealNumber
					funded
					customerNumber
					customerName
					dealType
					description
					financeEmployeeId
					financeEmployeeName
					financeEmployeeLocationId
					saleDate
					saleType
					salesEmployeeId
					salesEmployeeName
					salesEmployeeLocationId
					salesManagerId
					salesManagerName
					salesManagerLocationId
					stockNumber
					frontGrossProfit
					backGrossProfit
					units
					frontProducts {
						product
						value
					}
					backProducts {
						product
						value
					}
					external
					sourceLocationId
					origin {
						icon
						tooltip
					}
					dealerCodeFS
					identifiers {
						_id
						locationName
						dealerCodeFS
						locationId
						oemName
					}
				}
				carryOverFundingProgress {
					_id
					dealNumber
					funded
					customerNumber
					customerName
					dealType
					description
					financeEmployeeId
					financeEmployeeName
					financeEmployeeLocationId
					saleDate
					saleType
					salesEmployeeId
					salesEmployeeName
					salesEmployeeLocationId
					salesManagerId
					salesManagerName
					stockNumber
					frontGrossProfit
					backGrossProfit
					units
					frontProducts {
						product
						value
					}
					backProducts {
						product
						value
					}
					external
					sourceLocationId
					identifiers {
						_id
						locationName
						dealerCodeFS
						locationId
						oemName
					}
					origin {
						icon
						tooltip
					}
					dealerCodeFS
				}
			}
		}
	`;

	const variables = {
		id,
		locationId,
	};

	try {
		const response: GraphqlQueryResponse<"getSalesPerformancesByCommissionCycle", IGetSalesPerformanceResponse> =
			await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getSalesPerformancesByCommissionCycle ?? null;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
}

export interface ICrossStoreReconciliationResponse {
	locationId: string;
	commissionCycleId: string;
	salesPerformances: ISalesPerformanceExport[];
}

export async function getCrossStoreReconciliationPreview(locationId: string, commissionCycleId: string) {
	const query = gql`
		query getCrossStoreReconciliationPreview($locationId: String!, $commissionCycleId: String!) {
			getCrossStoreReconciliationPreview(locationId: $locationId, commissionCycleId: $commissionCycleId) {
				locationId
				commissionCycleId
				salesPerformances {
					_id
					exportedId
					exportedOn
					exportedBy
					exportedType
					exportedBalance
					salesPerformanceId
					locationId
					commissionCycleId
					payPeriodId
					funded
					saleDate
					dealNumber
					stockNumber
					salesEmployeeId
					salesEmployeeName
					salesEmployeeLocationId
					financeEmployeeId
					financeEmployeeName
					financeEmployeeLocationId
					salesManagerId
					salesManagerName
					salesManagerLocationId
					customerName
					customerNumber
					saleType
					dealType
					units
					sourceLocationId
					external
					frontGrossProfit
					backGrossProfit
					origin {
						icon
						tooltip
					}
					commissions {
						locationId
						userId
						employeeId
						employeeName
						position
						frontCommission
						backCommission
						grossCommission
						frontCommissionExport
						backCommissionExport
					}
				}
			}
		}
	`;

	const variables = {
		locationId,
		commissionCycleId,
	};

	try {
		const response: GraphqlQueryResponse<"getCrossStoreReconciliationPreview", ICrossStoreReconciliationResponse> =
			await apiInstance.post("/graphql", {
				query,
				variables,
			});
		return response.data.data?.getCrossStoreReconciliationPreview ?? null;
	} catch (err) {
		errorLogger(err as Error, {
			function: "getCrossStoreReconciliationPreview",
			message: "Failed to get the Cross Store Reconciliation Preview data",
			variables,
		});
		return null;
	}
}

export interface ICrossStoreReconciliationExport {
	exportedId: string;
	exportedBy: string;
	exportedType: string;
	exportedOn: string;
}
export async function getCrossStoreReconciliationExportHistory(
	locationId: string,
	commissionCycleId: string
): Promise<ICrossStoreReconciliationExport[] | null> {
	const query = gql`
		query getCrossStoreReconciliationExportHistory($commissionCycleId: String!, $locationId: String) {
			getCrossStoreReconciliationExportHistory(commissionCycleId: $commissionCycleId, locationId: $locationId) {
				exportedId
				exportedBy
				exportedType
				exportedOn
			}
		}
	`;

	const variables = {
		locationId,
		commissionCycleId,
	};

	try {
		const response: GraphqlQueryResponse<"getCrossStoreReconciliationExportHistory", ICrossStoreReconciliationExport[]> =
			await apiInstance.post("/graphql", {
				query,
				variables,
			});
		return response.data.data?.getCrossStoreReconciliationExportHistory ?? null;
	} catch (err) {
		errorLogger(err as Error, {
			function: "getCrossStoreReconciliationExportHistory",
			message: "Failed to get the Cross Store Reconciliation Export History data",
			variables,
		});
		return null;
	}
}
